import { c as _c } from "react/compiler-runtime";
import { ReactNode } from "react";
import PatientDisplay from "@/components/patient/patient-display";
import { calculateRelativeDuration, formatName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { UserType } from "@/types/enums";
import { WaitlistEntry, WaitlistStatus } from "@/types/waitlist";
import { formatWaitlistDateRange } from "./format-waitlist-date-range";
import { providerChoiceLabel } from "./waitlist-provider-choices";
import { waitlistTimePreferenceLabels } from "./waitlist-time-preference";
interface EntryDescriptionProps {
  entry: WaitlistEntry;
  actions?: ReactNode;
  selection?: ReactNode;
}
function EntryDescription(t0) {
  const $ = _c(60);
  const {
    entry,
    actions,
    selection,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== entry.patient) {
    t1 = entry.patient ? <PatientDisplay patient={{
      ...entry.patient,
      firstName: entry.patient.firstName ?? "",
      lastName: entry.patient.lastName ?? undefined,
      type: UserType.PATIENT
    }} layout="vertical" nameClassName="break-words text-h4" showMemberships={false} /> : "Unknown patient";
    $[0] = entry.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <h3 className="min-w-0 break-words text-h4 text-foreground">{t1}</h3>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== selection || $[5] !== t2) {
    t3 = <div className="flex min-w-0 items-center gap-2">{selection}{t2}</div>;
    $[4] = selection;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== actions) {
    t4 = actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>;
    $[7] = actions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex items-center justify-between gap-3">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== entry.assignments || $[13] !== entry.providers || $[14] !== entry.services) {
    let t7;
    if ($[16] !== entry.providers || $[17] !== entry.services) {
      t7 = (assignment, index) => <li key={index} className="break-words"><span className="text-body-strong text-foreground-secondary">{entry.services.find(service => service.id === assignment.serviceId)?.name ?? "Selected service"}</span><span className="block text-caption text-foreground-secondary">{providerChoiceLabel(assignment, entry.providers)}</span></li>;
      $[16] = entry.providers;
      $[17] = entry.services;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = entry.assignments.map(t7);
    $[12] = entry.assignments;
    $[13] = entry.providers;
    $[14] = entry.services;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <ol className="space-y-1">{t6}</ol>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = entry.status === WaitlistStatus.CONTACTED ? "Contacted \xB7 waiting" : "Waiting";
  let t9;
  if ($[21] !== t8) {
    t9 = <span>{t8}</span>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== entry.createdDate) {
    let t11;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = / ago$/;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = calculateRelativeDuration(new Date(entry.createdDate)).replace(t11, "");
    $[23] = entry.createdDate;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <span>{t10}</span>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t9) {
    t12 = <p className="text-caption text-foreground-tertiary"><span>{t9}{" "}{t11}</span></p>;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t5 || $[33] !== t7) {
    t13 = <div className="space-y-1">{t5}{t7}{t12}</div>;
    $[31] = t12;
    $[32] = t5;
    $[33] = t7;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== className) {
    t14 = cx("grid text-caption", className);
    $[35] = className;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <dt className="text-foreground-tertiary">Location</dt>;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = entry.locationName ?? (entry.locationId ? "Selected location" : "Any location");
  let t17;
  if ($[38] !== t16) {
    t17 = <dd className="break-words text-foreground-secondary">{t16}</dd>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <dt className="text-foreground-tertiary">Dates</dt>;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== entry) {
    t19 = formatWaitlistDateRange(entry);
    $[41] = entry;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19) {
    t20 = <dd className="text-foreground-secondary">{t19}</dd>;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <dt className="text-foreground-tertiary">Time</dt>;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = waitlistTimePreferenceLabels[entry.timePreference ?? "any"];
  let t23;
  if ($[46] !== t22) {
    t23 = <dd className="text-foreground-secondary">{t22}</dd>;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== children || $[49] !== t14 || $[50] !== t17 || $[51] !== t20 || $[52] !== t23) {
    t24 = <dl className={t14}>{children}{t15}{t17}{t18}{t20}{t21}{t23}</dl>;
    $[48] = children;
    $[49] = t14;
    $[50] = t17;
    $[51] = t20;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== entry.notes) {
    t25 = entry.notes && <div className="rounded-md bg-background-secondary px-1 py-0.5"><p className="whitespace-pre-wrap break-words text-caption text-foreground-secondary">{entry.notes}</p></div>;
    $[54] = entry.notes;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t13 || $[57] !== t24 || $[58] !== t25) {
    t26 = <div className="flex min-w-0 flex-1 flex-col gap-3">{t13}{t24}{t25}</div>;
    $[56] = t13;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
}
export function WaitlistMatchEntryDescription(t0) {
  const $ = _c(3);
  const {
    entry,
    actions
  } = t0;
  let t1;
  if ($[0] !== actions || $[1] !== entry) {
    t1 = <EntryDescription entry={entry} actions={actions} className="grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1" />;
    $[0] = actions;
    $[1] = entry;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function CompactWaitlistEntryDescription(t0) {
  const $ = _c(4);
  const {
    entry,
    actions,
    selection
  } = t0;
  let t1;
  if ($[0] !== actions || $[1] !== entry || $[2] !== selection) {
    t1 = <EntryDescription entry={entry} actions={actions} selection={selection} className="grid-cols-1 gap-y-0.5 [&>dd:not(:last-child)]:mb-1.5" />;
    $[0] = actions;
    $[1] = entry;
    $[2] = selection;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/**
 * One waitlist entry as a short row, for the Follow-Up drawer: who, what
 * they want, and when. Dates and the rest stay in the edit window.
 */
export function WaitlistEntryRow(t0) {
  const $ = _c(39);
  const {
    entry,
    actions,
    selection
  } = t0;
  let t1;
  if ($[0] !== entry.patient) {
    t1 = entry.patient ? formatName(entry.patient.firstName ?? "", entry.patient.lastName ?? "") : "Unknown patient";
    $[0] = entry.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  let t2;
  if ($[2] !== entry.createdDate) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = / ago$/;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = calculateRelativeDuration(new Date(entry.createdDate)).replace(t3, "");
    $[2] = entry.createdDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const waited = t2;
  const t3 = waitlistTimePreferenceLabels[entry.timePreference ?? "any"];
  let t4;
  if ($[5] !== t3) {
    t4 = t3.split(" (");
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = entry.locationName ?? (entry.locationId ? "Selected location" : null);
  const t6 = entry.status === WaitlistStatus.CONTACTED ? `texted an offer · waiting ${waited}` : `waiting ${waited}`;
  let t7;
  if ($[7] !== t4[0] || $[8] !== t5 || $[9] !== t6) {
    t7 = [t4[0], t5, t6].filter(Boolean);
    $[7] = t4[0];
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const meta = t7;
  let t8;
  if ($[11] !== selection) {
    t8 = selection && <span className="pt-0.5">{selection}</span>;
    $[11] = selection;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== name) {
    t9 = <span className="break-words text-body-strong text-foreground">{name}</span>;
    $[13] = name;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== entry.assignments || $[16] !== entry.providers || $[17] !== entry.services) {
    let t11;
    if ($[19] !== entry.providers || $[20] !== entry.services) {
      t11 = (assignment, index) => <li key={index} className="break-words text-caption text-foreground-secondary"><span>{entry.services.find(service => service.id === assignment.serviceId)?.name ?? "Selected service"}</span><span> · </span><span>{providerChoiceLabel(assignment, entry.providers)}</span></li>;
      $[19] = entry.providers;
      $[20] = entry.services;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    t10 = entry.assignments.map(t11);
    $[15] = entry.assignments;
    $[16] = entry.providers;
    $[17] = entry.services;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <ol>{t10}</ol>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = meta.join(" \xB7 ");
  let t13;
  if ($[24] !== t12) {
    t13 = <span className="break-words text-caption text-foreground-tertiary">{t12}</span>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== entry.notes) {
    t14 = entry.notes && <span className="whitespace-pre-wrap break-words text-caption text-foreground-secondary">{entry.notes}</span>;
    $[26] = entry.notes;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t13 || $[30] !== t14 || $[31] !== t9) {
    t15 = <div className="flex min-w-0 flex-1 flex-col">{t9}{t11}{t13}{t14}</div>;
    $[28] = t11;
    $[29] = t13;
    $[30] = t14;
    $[31] = t9;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== actions) {
    t16 = actions && <div className="flex shrink-0 items-center">{actions}</div>;
    $[33] = actions;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t8) {
    t17 = <div className="flex items-start gap-3">{t8}{t15}{t16}</div>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t8;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}